/* app/agency.jsx - ImyBoss Agency Panel dashboard (staff-only shell).
   Overview · Statistics · Users · Billing · Activity log · Settings · Tenants
   (read-only drill-down). Wired to /api/agency/* — tenant truth comes from
   the Management Panel's org spine (shared DB); engagements/work/activity are
   the panel's own agency_* tables. Every route is gated server-side to agency
   staff; anyone else gets a 403, surfaced as an "agency access only" state. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function MlIcon(props) { return React.createElement(window.MlIcon, props); }
  const DS = () => window.HuddleDesignSystem_775113;

  const fmt = (n) => Number(n || 0).toLocaleString();
  const compact = (n) => { n = Number(n || 0); return n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, "") + "K" : String(n); };
  const cap = (s) => String(s || "").replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
  const money = (n, cur) => (cur === "EUR" ? "€" : cur === "USD" ? "$" : (cur ? cur + " " : "")) + fmt(n);
  const timeAgo = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "";
    const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return s + "s ago";
    const m = Math.floor(s / 60); if (m < 60) return m + "m ago";
    const h = Math.floor(m / 60); if (h < 24) return h + "h ago";
    const d = Math.floor(h / 24); if (d < 30) return d + "d ago";
    return new Date(iso).toLocaleDateString();
  };

  // GET hook. err.status === 403 → caller renders the agency-only gate.
  function useApi(path) {
    const [state, setState] = useState({ data: null, err: null, loading: true });
    useEffect(() => {
      let alive = true;
      setState({ data: null, err: null, loading: true });
      api(path).then(d => alive && setState({ data: d, err: null, loading: false }))
               .catch(e => alive && setState({ data: null, err: e, loading: false }));
      return () => { alive = false; };
    }, [path]);
    return state;
  }
  const Loading = ({ label = "Loading…" }) => <div style={{ padding: "32px 0", textAlign: "center", color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>{label}</div>;
  function StateNote({ err }) {
    if (err && err.status === 403) {
      return React.createElement(DS().Banner, { tone: "info", title: "Agency access only" },
        "The agency dashboard is available to the IMYBOSS agency account. Your account doesn't have cross-tenant access.");
    }
    return React.createElement(DS().Banner, { tone: "danger", title: "Couldn't load" }, (err && err.message) || "Request failed.");
  }
  // Renders loading / error / 403, or the children once data is ready.
  function Gate({ loading, err, children }) {
    if (loading) return <Loading />;
    if (err) return <StateNote err={err} />;
    return children;
  }

  const statusPill = (s) => {
    const { Badge } = DS();
    if (s === "active" || s === "trialing") return <Badge tone="success" dot>{cap(s)}</Badge>;
    if (s === "past_due") return <Badge tone="danger" dot>Past due</Badge>;
    return <Badge tone="warning" dot>{cap(s) || "Unknown"}</Badge>;
  };

  // "Access until" cell for a spine subscription's currentPeriodEnd (SD-014 §8).
  // Expiry is enforced for real now that the Management Panel's half of SD-014
  // is live: its entitlements API grants nothing once currentPeriodEnd is past.
  // So a past date is not merely a date that slipped — the customer is shut out
  // of the app today. Hence "Expired", and the danger colour.
  const accessUntil = (iso) => {
    const t = iso ? new Date(iso).getTime() : NaN;
    if (!Number.isFinite(t)) return <span className="hds-table__muted" title="No end date recorded on this subscription.">—</span>;
    const when = new Date(t).toLocaleDateString();
    const days = Math.ceil((t - Date.now()) / 86400000);
    if (days < 0) return <span style={{ color: "var(--danger)", fontWeight: 600 }} title={"Expired " + Math.abs(days) + " day(s) ago. Nobody at this client can use the app until the end date is moved forward."}>Expired {when}</span>;
    if (days <= 14) return <span style={{ color: "var(--warning)", fontWeight: 600 }} title={"Access expires in " + days + " day(s), and they lose the app that day unless it is extended."}>{when} · {days}d</span>;
    return <span className="hds-table__muted">{when}</span>;
  };

  // ---- manual access management (SD-014 §7) ----------------------------------
  // Staff can comp an org, record a payment taken outside Stripe, or push an
  // existing grant's end date out. Every write goes to the Management Panel via
  // our own /agency/account/:orgId/access/* routes; this panel never writes the
  // spine itself. Dates cross the wire as the raw <input type=date> value.

  // Local calendar day as YYYY-MM-DD (never via toISOString — that shifts to UTC
  // and can hand the backend yesterday).
  const ymd = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
  };
  // Earliest end date the backend will take. It parses YYYY-MM-DD as UTC
  // midnight and refuses anything at or before now, so in a timezone behind UTC
  // the local "tomorrow" can still land in the past — step on until it doesn't.
  const earliestEnd = () => {
    const d = new Date();
    do { d.setDate(d.getDate() + 1); } while (Date.parse(ymd(d)) <= Date.now());
    return ymd(d);
  };
  // Suggested end date: a month on from `from` if that is still in the future,
  // otherwise a month from today. A month is the usual comp / extension length;
  // staff can change it in the dialog.
  const defaultUntil = (from) => {
    const t = from ? new Date(from).getTime() : NaN;
    const d = new Date(Number.isFinite(t) && t > Date.now() ? t : Date.now());
    d.setMonth(d.getMonth() + 1);
    return ymd(d);
  };
  // YYYY-MM-DD → a date a human reads, parsed as a local day (not UTC).
  const prettyDay = (s) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString() : "";
  };
  const prettyIso = (iso) => { const t = iso ? new Date(iso).getTime() : NaN; return Number.isFinite(t) ? new Date(t).toLocaleDateString() : ""; };

  // Shared client-side checks, so an obvious mistake never leaves the browser.
  // `dateKey` is the field holding the end date ("until", or "period_end" when
  // marking an org as paid). The backend validates the same things — these just
  // answer immediately.
  const checkGrant = (v, dateKey = "until") => {
    const when = v[dateKey];
    if (!v.reason || !v.reason.trim()) throw new Error("A reason is required — it is stored on the subscription and it is the only record of why this was done.");
    if (!when) throw new Error("An end date is required. A manual grant always ends on a date.");
    // Same comparison the server makes, so the two never disagree.
    if (!(Date.parse(when) > Date.now())) throw new Error("The end date has to be in the future — " + prettyDay(earliestEnd()) + " at the earliest. Anything sooner would be over the moment you saved it.");
  };

  // Every verdict from the access endpoints arrives as a readable sentence the
  // backend wrote (or passed through from the Management Panel), so branch on
  // the status, show that sentence, and only add guidance where the sentence
  // alone doesn't say what to do next. Never match on the text — it changes.
  const accessErr = (e) => {
    const s = e && e.status;
    let said = String((e && e.message) || "").trim();
    if (said) said = said.charAt(0).toUpperCase() + said.slice(1);
    // 400 (bad date, date not moving forward) and 404 (no such org/app, or no
    // subscription to extend) are complete answers on their own.
    if (s === 400 || s === 404 || s === 403) return said || "The panel wouldn't accept that change.";
    if (s === 409) return (said || "This subscription is managed by Stripe.") + " Nothing was changed — overriding it from here would leave the customer billed by Stripe and comped by us at the same time.";
    if (s === 502) return (said || "Management panel unreachable") + " — nothing was changed. Try again in a moment; if it keeps failing the Management Panel is down and someone needs to look at it.";
    if (s === 503) return (said || "Manual access management is not configured") + " — nothing was changed. Its Management Panel URL and API key have to be set for this panel before access can be granted here.";
    return said || "The change could not be saved.";
  };

  function Brand() {
    return (
      <span className="ml-brand">
        <span className="ml-brand__mark" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><circle cx="11" cy="11" r="2.6" /><path d="M16.5 16.5 21 21" /></svg></span>
        <span style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}><span className="ml-brand__name">ImyBoss</span><span className="ml-brand__by">Agency Panel</span></span>
      </span>
    );
  }

  const NAV = [
    { label: "Agency", items: [["overview", "Overview", "dashboard"], ["statistics", "Statistics", "bar-chart"], ["activity", "Activity log", "clock"]] },
    { label: "Delivery", items: [["work", "Work board", "check-square"]] },
    { label: "Business", items: [["tenants", "Tenants", "building"], ["users", "Users", "users"], ["billing", "Billing", "credit-card"]] },
    { label: "", items: [["settings", "Settings", "settings"]] },
  ];

  // ---- screens ----
  function Overview({ go }) {
    const { StatCard } = DS();
    const ov = useApi("/agency/overview");
    const bill = useApi("/agency/billing");
    return (
      <div>
        <div className="ml-pagehead"><div className="ml-pagehead__titles"><h1 className="ml-pagehead__title">Overview</h1><p className="ml-pagehead__sub">Your agency at a glance.</p></div></div>
        <Gate loading={ov.loading} err={ov.err}>
          {ov.data && (() => {
            const totals = ov.data.totals || {};
            const clients = (ov.data.accounts || []).filter(a => !a.is_agency);
            // Needs attention: billing trouble, or an engagement waiting on a
            // handshake step (requested → propose a manager; proposed → client
            // approval pending).
            const attention = clients.filter(a => a.status === "past_due" || ["requested", "proposed"].includes(a.engagement_status));
            const mrr = bill.data ? money(bill.data.mrr, bill.data.currency) : "-";
            return (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 16, marginBottom: 20 }}>
                  <StatCard label="Client tenants" value={fmt(clients.length)} icon="building" />
                  <StatCard label="Customer MRR" value={mrr} icon="dollar" deltaNote={bill.loading ? "loading…" : "estimated"} />
                  <StatCard label="Active engagements" value={fmt(totals.engagements_active)} icon="check-circle" />
                  <StatCard label="Open work items" value={fmt(totals.open_work)} icon="check-square" />
                </div>
                <div className="hds-card">
                  <div className="hds-card__header"><div className="hds-card__title" style={{ fontSize: "var(--text-md)" }}>Needs attention</div></div>
                  <div style={{ padding: 8 }}>
                    {attention.length === 0 && <div style={{ padding: "16px 8px", color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>All tenants healthy.</div>}
                    {attention.map(t => (
                      <button key={t.id} className="ml-menu__item" onClick={() => go("tenant:" + t.id)} style={{ borderRadius: 8 }}>
                        <span className="ml-sitecard__flag" style={{ width: 30, height: 30, fontSize: 13 }}>{(t.name || "?")[0]}</span>
                        <span className="ml-menu__item-meta"><span style={{ fontWeight: 600 }}>{t.name}</span><small>{t.plan_label || cap(t.plan)}{t.engagement_status ? " · engagement " + cap(t.engagement_status) : ""}</small></span>
                        <span style={{ marginLeft: "auto" }}>{statusPill(t.status)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}
        </Gate>
      </div>
    );
  }

  function Statistics() {
    const { StatCard } = DS();
    const { data, err, loading } = useApi("/agency/stats");
    const TILES = [
      ["accounts", "Tenants", "building"], ["users", "Suite users", "user"], ["members", "Memberships", "users"],
      ["apps", "Apps in catalog", "layers"], ["subs_active", "Active subscriptions", "credit-card"], ["seats_assigned", "Seats assigned", "check-circle"],
      ["invitations_pending", "Pending invites", "send"], ["staff", "Agency staff", "briefcase"], ["engagements_active", "Active engagements", "target"],
      ["engagements_pending", "Pending engagements", "clock"], ["work_open", "Open work items", "check-square"], ["approvals_pending", "Pending approvals", "shield-check"],
    ];
    return (
      <div>
        <div className="ml-pagehead"><div className="ml-pagehead__titles"><h1 className="ml-pagehead__title">Statistics</h1><p className="ml-pagehead__sub">Platform data across your whole book of business.</p></div></div>
        <Gate loading={loading} err={err}>
          {data && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px,1fr))", gap: 16 }}>
              {TILES.map(([k, label, icon]) => (
                <StatCard key={k} label={label} value={data.stats[k] == null ? "-" : fmt(data.stats[k])} icon={icon} />
              ))}
            </div>
          )}
        </Gate>
      </div>
    );
  }

  // App-styled form modal (reuses the BI modal shell) for agency invite / role.
  // `note` is an optional summary rendered under the fields, right above the
  // confirm button — a string/node, or a function of the current values when it
  // has to restate what the staff member just typed (access grants do).
  // `noteTone` escalates that banner when the current answers deserve a second
  // look. `onValues` reports the live values to the caller, which the access
  // dialogs need so they can load the chosen app's plans.
  function AgyFormModal({ title, sub, fields, note, noteTone = "info", onValues, confirmLabel = "Save", onSubmit, onClose }) {
    const { Field, Input, Select, Banner } = DS();
    const Modal = window.MlBiModals._Modal;
    const [vals, setVals] = useState(() => Object.fromEntries(fields.map(f => [f.key, f.value != null ? f.value : (f.options ? f.options[0].value : "")])));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    // Deliberately keyed on `vals` alone: onValues is re-created every render by
    // its caller, and the caller only ever stores what it is handed.
    useEffect(() => { if (onValues) onValues(vals); }, [vals]);
    const submit = async () => { setBusy(true); setErr(null); try { await onSubmit(vals); } catch (e) { setErr((e && e.message) || "Failed"); setBusy(false); } };
    const noteBody = typeof note === "function" ? note(vals) : note;
    const tone = typeof noteTone === "function" ? noteTone(vals) : noteTone;
    return React.createElement(Modal, { sm: true, title, sub, onClose: busy ? () => {} : onClose,
      foot: <><button className="bi-btn" onClick={onClose} disabled={busy}>Cancel</button><button className="bi-btn bi-btn--primary" onClick={submit} disabled={busy}>{busy ? "Working…" : confirmLabel}</button></> },
      <div className="bi-modal__body" style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 4, paddingBottom: 8 }}>
        {err && <Banner tone="danger" title="Error">{err}</Banner>}
        {fields.map((f, i) => (
          <Field key={f.key} label={f.label} hint={f.hint} required={!!f.required}>
            {f.options
              ? <Select value={vals[f.key]} onChange={e => setVals(s => ({ ...s, [f.key]: e.target.value }))}>{f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</Select>
              : <Input type={f.type || "text"} value={vals[f.key]} min={f.min} autoFocus={i === 0} placeholder={f.placeholder} onChange={e => setVals(s => ({ ...s, [f.key]: e.target.value }))} />}
          </Field>
        ))}
        {noteBody && <Banner tone={tone} title="What this does:">{noteBody}</Banner>}
      </div>
    );
  }

  // Reference matrix of what each agency user type can do. UI only for now —
  // writes are still enforced server-side by the agency API (owner/admin), and
  // the Analyst type is read-only. Per-capability enforcement is a roadmap item.
  const AGENCY_ROLES = [
    { id: "owner", label: "Owner" },
    { id: "admin", label: "Admin / ops" },
    { id: "staff", label: "Account manager" },
    { id: "member", label: "Analyst" },
  ];
  const AGENCY_PERMS = [
    { label: "View dashboards, statistics & activity", desc: "Read the agency overview, statistics and activity log.", roles: ["owner", "admin", "staff", "member"] },
    { label: "Act on assigned clients", desc: "Open a client workspace (act-as-tenant) for an approved engagement they lead.", roles: ["owner", "admin", "staff"] },
    { label: "Manage engagements", desc: "Open, assign, reassign and revoke managed-service engagements.", roles: ["owner", "admin"] },
    { label: "Invite & manage staff", desc: "Invite agency staff and change a staff member's user type.", roles: ["owner", "admin"] },
    { label: "Grant or withdraw ownership", desc: "Promote a staff member to Owner, or remove ownership.", roles: ["owner"] },
    { label: "Review billing", desc: "See agency billing and client subscription health.", roles: ["owner", "admin"] },
    { label: "Grant, extend or record access", desc: "Give a client free access to an app, record a payment taken outside Stripe, or move an access end date forward. Owner and Admin / ops only, the same line as Review billing \u2014 an account manager who cannot see billing does not decide who pays.", roles: ["owner", "admin"] },
    { label: "Agency settings", desc: "Change agency-wide settings.", roles: ["owner", "admin"] },
  ];
  function PermissionsPanel() {
    const { Banner } = DS();
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Banner tone="info" title="Reference only">
            These are the built-in agency user types. Writes are enforced on the server (Owner and Admin can make changes; Analyst is read-only). Fine-grained, per-capability control is on the roadmap.
          </Banner>
        </div>
        <div className="hds-table-wrap">
          <div className="ml-table-toolbar"><strong style={{ fontSize: "var(--text-sm)" }}>What each user type can do</strong></div>
          <table className="hds-table">
            <thead>
              <tr>
                <th>Capability</th>
                {AGENCY_ROLES.map(r => <th key={r.id} style={{ textAlign: "center", width: 130 }}>{r.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {AGENCY_PERMS.map((p, i) => (
                <tr key={i}>
                  <td><div className="ml-rowtitle">{p.label}</div><div className="hds-table__muted" style={{ fontSize: "var(--text-xs)", marginTop: 2 }}>{p.desc}</div></td>
                  {AGENCY_ROLES.map(r => (
                    <td key={r.id} style={{ textAlign: "center" }}>
                      {p.roles.includes(r.id)
                        ? <span style={{ color: "var(--primary)", fontWeight: 600 }} title={r.label + " can: " + p.label}>✓</span>
                        : <span style={{ color: "var(--text-disabled)" }} title={r.label + " cannot: " + p.label}>–</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", marginTop: 12 }}>
          Only an Owner can grant or withdraw ownership, and the agency keeps at least one Owner.
        </p>
      </div>
    );
  }

  function UsersScreen() {
    const { Badge, Button, IconButton, Tabs } = DS();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);   // {type:"invite"} | {type:"role",m} | {notice}
    const [tab, setTab] = useState("staff");
    const load = useCallback(() => { setLoading(true); setErr(null); api("/agency/assignments").then(d => { setData(d); setLoading(false); }).catch(e => { setErr(e); setLoading(false); }); }, []);
    useEffect(() => { load(); }, [load]);
    const managers = (data && data.managers) || [];
    const engagements = (data && data.engagements) || [];
    const countFor = (uid) => engagements.filter(e => e.manager_user_id === uid && e.status === "active").length;
    const ROLE_OPTS = [{ value: "owner", label: "Owner" }, { value: "admin", label: "Admin / ops" }, { value: "staff", label: "Account manager" }, { value: "member", label: "Analyst" }];
    return (
      <div>
        <div className="ml-pagehead"><div className="ml-pagehead__titles"><h1 className="ml-pagehead__title">Users</h1><p className="ml-pagehead__sub">Agency staff and their client engagements.</p></div>{tab === "staff" && <div className="ml-pagehead__actions"><Button variant="secondary" leadingIcon="plus" onClick={async () => { try { const ov = await api("/agency/overview"); const free = (ov.accounts || []).filter(a => !a.engagement_status); setModal({ type: "engage", orgs: free }); } catch (e) { setModal({ type: "engage", orgs: [] }); } }}>New engagement</Button><Button variant="primary" leadingIcon="plus" onClick={() => setModal({ type: "invite" })}>Invite staff</Button></div>}</div>
        <div style={{ marginBottom: 20 }}><Tabs tabs={[{ id: "staff", label: "Staff", count: managers.length || undefined }, { id: "permissions", label: "Permissions" }]} value={tab} onChange={setTab} /></div>
        {tab === "permissions" ? <PermissionsPanel /> : (
        <Gate loading={loading} err={err}>
          {data && (
            <>
              <div className="hds-table-wrap" style={{ marginBottom: 20 }}>
                <div className="ml-table-toolbar"><strong style={{ fontSize: "var(--text-sm)" }}>Staff</strong></div>
                <table className="hds-table">
                  <thead><tr><th>Staff</th><th>Role</th><th>Active engagements</th><th style={{ width: 60 }}></th></tr></thead>
                  <tbody>{managers.length === 0 ? <tr><td colSpan={4} className="hds-table__muted">No agency staff yet.</td></tr> : managers.map(m => (
                    <tr key={m.id}><td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><span className="hds-avatar hds-avatar--sm">{((m.name || m.email || "?").match(/\b\w/g) || ["?"]).join("").slice(0, 2).toUpperCase()}</span><span className="ml-rowtitle">{m.name || m.email}</span></div></td><td><Badge tone={m.role === "owner" ? "primary" : "neutral"}>{cap(m.role)}</Badge></td><td className="hds-table__muted">{countFor(m.id)}</td><td><div className="ml-rowactions"><IconButton icon="edit" label="Change type" size="sm" onClick={() => setModal({ type: "role", m })} /></div></td></tr>
                  ))}</tbody>
                </table>
              </div>
              <div className="hds-table-wrap">
                <div className="ml-table-toolbar"><strong style={{ fontSize: "var(--text-sm)" }}>Engagements</strong></div>
                <table className="hds-table">
                  <thead><tr><th>Tenant</th><th>Account manager</th><th>Status</th><th style={{ width: 140 }}></th></tr></thead>
                  <tbody>{engagements.length === 0 ? <tr><td colSpan={4} className="hds-table__muted">No managed engagements. They start when a client requests managed service.</td></tr> : engagements.map(e => (
                    <tr key={e.id}><td className="hds-table__primary">{e.account_name}</td><td className="hds-table__muted">{e.manager_name || e.manager_email || "Unassigned"}</td><td><Badge tone={e.status === "active" ? "success" : e.status === "declined" ? "danger" : "warning"} dot>{cap(e.status)}</Badge></td><td><div className="ml-rowactions" style={{ justifyContent: "flex-end" }}>{e.status !== "active" && <Button variant="secondary" size="sm" onClick={() => setModal({ type: "propose", eng: e })}>{e.manager_user_id ? "Reassign" : "Assign"}</Button>}<IconButton icon="trash" label="Revoke" size="sm" onClick={() => setModal({ type: "revoke", eng: e })} /></div></td></tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
        </Gate>
        )}
        {modal && modal.type === "invite" && <AgyFormModal title="Invite agency staff" sub="They'll get an email invite to join the agency." confirmLabel="Send invite"
          fields={[{ key: "email", label: "Email", placeholder: "name@imyboss.com" }, { key: "role", label: "Invite as", value: "admin", options: [{ value: "admin", label: "Admin / ops" }, { value: "staff", label: "Account manager" }, { value: "member", label: "Analyst" }] }]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => { if (!v.email || !v.email.trim()) throw new Error("Email is required."); await api("/agency/staff/invite", { method: "POST", body: JSON.stringify({ email: v.email.trim(), role: v.role }) }); setModal(null); load(); }} />}
        {modal && modal.type === "engage" && <AgyFormModal title="New engagement" sub="Open a managed-service engagement for a client (recorded as Requested; the client confirms from their workspace panel)." confirmLabel="Open engagement"
          fields={[{ key: "org_id", label: "Client", options: modal.orgs.length ? modal.orgs.map(o => ({ value: o.id, label: o.name })) : [{ value: "", label: "No clients without an open engagement" }] }]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => { if (!v.org_id) throw new Error("Pick a client."); await api("/agency/assignments", { method: "POST", body: JSON.stringify({ org_id: v.org_id }) }); setModal(null); load(); }} />}
        {modal && modal.type === "role" && <AgyFormModal title="Change user type" sub={modal.m.email || modal.m.name} confirmLabel="Update"
          fields={[{ key: "role", label: "User type", value: modal.m.role, options: ROLE_OPTS }]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => { await api(`/agency/staff/${modal.m.id}`, { method: "PATCH", body: JSON.stringify({ role: v.role }) }); setModal(null); load(); }} />}
        {modal && modal.type === "propose" && <AgyFormModal title="Assign account manager" sub={"Propose a manager for " + modal.eng.account_name + ". The client approves before it goes active."} confirmLabel="Propose"
          fields={[{ key: "manager_user_id", label: "Account manager", value: modal.eng.manager_user_id ? String(modal.eng.manager_user_id) : undefined, options: managers.length ? managers.map(m => ({ value: String(m.id), label: (m.name || m.email) + " — " + cap(m.role) })) : [{ value: "", label: "No agency staff yet — invite one first" }] }]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => { if (!v.manager_user_id) throw new Error("Pick a staff member to assign."); await api(`/agency/assignments/${modal.eng.id}/propose`, { method: "POST", body: JSON.stringify({ manager_user_id: Number(v.manager_user_id) }) }); setModal(null); load(); }} />}
        {modal && modal.type === "revoke" && window.MlBiModals && React.createElement(window.MlBiModals.ConfirmDialog, { danger: true, title: "Revoke engagement", body: "Revoke the managed engagement for " + modal.eng.account_name + "? The account manager loses access to this client.", confirmLabel: "Revoke", onClose: () => setModal(null), onConfirm: async () => { try { await api("/agency/assignments/" + modal.eng.id, { method: "DELETE" }); } catch { /* */ } setModal(null); load(); } })}
      </div>
    );
  }

  function Billing() {
    const { Badge } = DS();
    const { data, err, loading } = useApi("/agency/billing");
    return (
      <div>
        <div className="ml-pagehead"><div className="ml-pagehead__titles"><h1 className="ml-pagehead__title">Billing</h1><p className="ml-pagehead__sub">Customer MRR across your tenants.</p></div></div>
        <Gate loading={loading} err={err}>
          {data && (() => {
            const rows = (data.accounts || []).filter(a => !a.is_agency);
            return (
              <>
                <div className="hds-card hds-card--pad" style={{ marginBottom: 16, maxWidth: 260 }}><div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", marginBottom: 4 }}>Total MRR (estimated)</div><div className="ml-bignum">{money(data.mrr, data.currency)}</div></div>
                <div className="hds-table-wrap">
                  <table className="hds-table">
                    <thead><tr><th>Tenant</th><th>Plan</th><th>Monthly</th><th>Status</th></tr></thead>
                    <tbody>{rows.length === 0 ? <tr><td colSpan={4} className="hds-table__muted">No client tenants yet.</td></tr> : rows.map(t => (
                      <tr key={t.id}><td className="hds-table__primary">{t.name}</td><td><Badge tone="neutral">{t.plan_label || cap(t.plan)}</Badge></td><td style={{ fontVariantNumeric: "tabular-nums" }}>{t.custom ? "Custom" : money(t.monthly, data.currency) + "/mo"}</td><td>{statusPill(t.status)}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </Gate>
      </div>
    );
  }

  function Activity() {
    const { data, err, loading } = useApi("/agency/activity?limit=60");
    const iconFor = (action) => {
      const a = String(action || "");
      if (a.includes("delete") || a.includes("revoke")) return "trash";
      if (a.includes("impersonate")) return "eye";
      if (a.includes("invite") || a.includes("member")) return "user";
      if (a.includes("publish")) return "check-circle";
      return "clock";
    };
    return (
      <div>
        <div className="ml-pagehead"><div className="ml-pagehead__titles"><h1 className="ml-pagehead__title">Activity log</h1><p className="ml-pagehead__sub">Everything happening across your tenants.</p></div></div>
        <Gate loading={loading} err={err}>
          {data && (
            <div className="hds-card hds-card--pad" style={{ maxWidth: 760 }}>
              {(data.events || []).length === 0 ? <div style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>No recent activity.</div> : (
                <div className="ml-timeline">
                  {data.events.map((e, i) => (
                    <div className="ml-tl" key={e.id}>
                      <span className="ml-tl__dot"><MlIcon name={iconFor(e.action)} size={15} /></span>
                      {i < data.events.length - 1 && <span className="ml-tl__line" />}
                      <div className="ml-tl__body"><div className="ml-tl__text">{e.actor_email ? <b>{e.actor_email}</b> : "System"} · {e.action}{e.account_name ? <> on <b>{e.account_name}</b></> : null}</div><div className="ml-tl__time">{timeAgo(e.created_at)}</div></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Gate>
      </div>
    );
  }

  function Settings() {
    const { Field, Input, Switch, Button } = DS();
    const { data, err, loading } = useApi("/agency/settings");
    const [trusted, setTrusted] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveErr, setSaveErr] = useState(null);
    useEffect(() => { if (data) setTrusted(!!data.trusted_default); }, [data]);
    const save = async () => {
      setSaveErr(null);
      try { await api("/agency/settings", { method: "POST", body: JSON.stringify({ trusted_default: trusted }) }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
      catch (e) { setSaveErr((e && e.message) || "Could not save."); }
    };
    return (
      <div style={{ maxWidth: 680 }}>
        <div className="ml-pagehead"><div className="ml-pagehead__titles"><h1 className="ml-pagehead__title">Agency settings</h1></div></div>
        {saveErr && <div style={{ marginBottom: 12 }}>{React.createElement(DS().Banner, { tone: "danger", title: "Couldn't save" }, saveErr)}</div>}
        <Gate loading={loading} err={err}>
          {data && (
            <div className="hds-card"><div className="hds-card__header"><div className="hds-card__title">Agency profile</div></div><div className="hds-card__body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Field label="Agency name"><Input value={data.agency_name || ""} readOnly /></Field>
              <Switch label="Account managers are trusted by default (pre-authorize approve-tier actions)" checked={trusted} onChange={(e) => setTrusted(e.target.checked)} />
              <div><Button variant="primary" onClick={save}>{saved ? "Saved" : "Save settings"}</Button></div>
            </div></div>
          )}
        </Gate>
      </div>
    );
  }

  // ---- Delivery: work board ----
  const WORK_STATUS_OPTS = [
    { value: "todo", label: "To do" }, { value: "in_progress", label: "In progress" },
    { value: "review", label: "Review" }, { value: "blocked", label: "Blocked" }, { value: "done", label: "Done" },
  ];
  const workTone = (s) => s === "blocked" ? "danger" : s === "review" ? "warning" : s === "done" ? "success" : s === "in_progress" ? "primary" : "neutral";

  // Inline status dropdown — PATCHes the item, then tells the caller to reload.
  function WorkStatusSelect({ item, onChanged }) {
    const { Select } = DS();
    const [busy, setBusy] = useState(false);
    return (
      <Select value={item.status} disabled={busy} onChange={async (e) => {
        setBusy(true);
        try { await api(`/agency/work/${item.id}`, { method: "PATCH", body: JSON.stringify({ status: e.target.value }) }); } catch { /* surface via reload */ }
        setBusy(false); onChanged();
      }}>
        {WORK_STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
    );
  }

  // Shared work-item table (used by the board and the tenant drill-down).
  // Columns adapt: the board shows the tenant, the drill-down doesn't.
  function WorkTable({ items, showTenant, staff, onReload, onOpenTenant }) {
    const { IconButton } = DS();
    const [modal, setModal] = useState(null);
    const staffOpts = [{ value: "", label: "Unassigned" }, ...(staff || []).map(s => ({ value: String(s.id), label: s.name || s.email }))];
    return (
      <>
        <table className="hds-table">
          <thead><tr><th>Work item</th><th>App</th>{showTenant && <th>Tenant</th>}<th>Assignee</th><th>Due</th><th style={{ width: 150 }}>Status</th><th style={{ width: 80 }}></th></tr></thead>
          <tbody>{items.length === 0 ? <tr><td colSpan={showTenant ? 7 : 6} className="hds-table__muted">No work items.</td></tr> : items.map(w => (
            <tr key={w.id}>
              <td className="hds-table__primary">{w.title}</td>
              <td className="hds-table__muted">{w.app_id || "—"}</td>
              {showTenant && <td className="hds-table__muted">{onOpenTenant ? <a href="#" onClick={(e) => { e.preventDefault(); onOpenTenant(w.org_id); }}>{w.account_name}</a> : w.account_name}</td>}
              <td className="hds-table__muted">{w.assignee_name || w.assignee_email || "Unassigned"}</td>
              <td className="hds-table__muted">{w.due_date ? String(w.due_date).slice(0, 10) : "—"}</td>
              <td><WorkStatusSelect item={w} onChanged={onReload} /></td>
              <td><div className="ml-rowactions" style={{ justifyContent: "flex-end" }}>
                <IconButton icon="edit" label="Edit" size="sm" onClick={() => setModal({ type: "edit", w })} />
                <IconButton icon="trash" label="Delete" size="sm" onClick={() => setModal({ type: "delete", w })} />
              </div></td>
            </tr>
          ))}</tbody>
        </table>
        {modal && modal.type === "edit" && <AgyFormModal title="Edit work item" sub={modal.w.title} confirmLabel="Save"
          fields={[
            { key: "title", label: "Title", value: modal.w.title },
            { key: "description", label: "Description", value: modal.w.description || "" },
            { key: "assignee_staff_id", label: "Assignee", value: modal.w.assignee_staff_id ? String(modal.w.assignee_staff_id) : "", options: staffOpts },
            { key: "due_date", label: "Due date", type: "date", value: modal.w.due_date ? String(modal.w.due_date).slice(0, 10) : "" },
          ]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => { await api(`/agency/work/${modal.w.id}`, { method: "PATCH", body: JSON.stringify(v) }); setModal(null); onReload(); }} />}
        {modal && modal.type === "delete" && window.MlBiModals && React.createElement(window.MlBiModals.ConfirmDialog, {
          danger: true, title: "Delete work item", body: `Delete "${modal.w.title}"? This cannot be undone.`, confirmLabel: "Delete",
          onClose: () => setModal(null),
          onConfirm: async () => { try { await api(`/agency/work/${modal.w.id}`, { method: "DELETE" }); } catch { /* */ } setModal(null); onReload(); } })}
      </>
    );
  }

  function WorkBoard({ go }) {
    const { Button, Select } = DS();
    const [data, setData] = useState(null);      // {items, staff, engagements, apps}
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [appFilter, setAppFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [modal, setModal] = useState(null);
    const load = useCallback(async () => {
      setErr(null);
      try {
        const q = new URLSearchParams();
        if (appFilter) q.set("app_id", appFilter);
        if (statusFilter) q.set("status", statusFilter);
        const [work, asg, apps] = await Promise.all([
          api("/agency/work" + (q.toString() ? "?" + q.toString() : "")),
          api("/agency/assignments"), api("/agency/apps"),
        ]);
        setData({ items: work.items || [], staff: asg.managers || [], engagements: (asg.engagements || []).filter(e => ["requested", "proposed", "active"].includes(e.status)), apps: apps.apps || [] });
      } catch (e) { setErr(e); }
      setLoading(false);
    }, [appFilter, statusFilter]);
    useEffect(() => { setLoading(true); load(); }, [load]);

    // Group rows by app for the per-app board view.
    const groups = [];
    if (data) for (const it of data.items) {
      const key = it.app_id || "";
      let g = groups.find(x => x.key === key);
      if (!g) { g = { key, label: key ? ((data.apps.find(a => a.id === key) || {}).name || key) : "No app", items: [] }; groups.push(g); }
      g.items.push(it);
    }

    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles"><h1 className="ml-pagehead__title">Work board</h1><p className="ml-pagehead__sub">Delivery work across every engagement, cut per app.</p></div>
          <div className="ml-pagehead__actions"><Button variant="primary" leadingIcon="plus" onClick={() => setModal({ type: "new" })}>New work item</Button></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 16, maxWidth: 480 }}>
          <Select value={appFilter} onChange={(e) => setAppFilter(e.target.value)}>
            <option value="">All apps</option>
            {(data ? data.apps : []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Open (not done)</option>
            {WORK_STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <Gate loading={loading} err={err}>
          {data && (groups.length === 0
            ? <div className="hds-card hds-card--pad" style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>No work items match. Create one with "New work item" — it needs an open engagement (Users → Engagements).</div>
            : groups.map(g => (
              <div className="hds-table-wrap" style={{ marginBottom: 20 }} key={g.key}>
                <div className="ml-table-toolbar"><strong style={{ fontSize: "var(--text-sm)" }}>{g.label}</strong><span style={{ marginLeft: 8, color: "var(--text-muted)", fontSize: "var(--text-xs)" }}>{g.items.length} item{g.items.length === 1 ? "" : "s"}</span></div>
                <WorkTable items={g.items} showTenant staff={data.staff} onReload={load} onOpenTenant={(orgId) => go("tenant:" + orgId)} />
              </div>
            )))}
        </Gate>
        {modal && modal.type === "new" && data && <AgyFormModal title="New work item" sub="Delivery work is tracked per engagement and tagged by app." confirmLabel="Create"
          fields={[
            { key: "engagement_id", label: "Engagement (client)", options: data.engagements.length ? data.engagements.map(e => ({ value: String(e.id), label: e.account_name })) : [{ value: "", label: "No open engagements — open one first (Users)" }] },
            { key: "title", label: "Title", placeholder: "e.g. April content batch" },
            { key: "app_id", label: "App", value: "", options: [{ value: "", label: "No app" }, ...data.apps.map(a => ({ value: a.id, label: a.name }))] },
            { key: "assignee_staff_id", label: "Assignee", value: "", options: [{ value: "", label: "Unassigned" }, ...data.staff.map(s => ({ value: String(s.id), label: s.name || s.email }))] },
            { key: "due_date", label: "Due date", type: "date", value: "" },
          ]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => {
            if (!v.engagement_id) throw new Error("Pick an engagement.");
            if (!v.title || !v.title.trim()) throw new Error("Title is required.");
            await api("/agency/work", { method: "POST", body: JSON.stringify({ ...v, engagement_id: Number(v.engagement_id) }) });
            setModal(null); load();
          }} />}
      </div>
    );
  }

  function Tenants({ go }) {
    const { data, err, loading } = useApi("/agency/overview");
    const { IconButton, Input } = DS();
    const [q, setQ] = useState("");
    return (
      <div>
        <div className="ml-pagehead"><div className="ml-pagehead__titles"><h1 className="ml-pagehead__title">Tenants</h1><p className="ml-pagehead__sub">Client accounts. Open one for a read-only drill-down.</p></div></div>
        <Gate loading={loading} err={err}>
          {data && (() => {
            const rows = (data.accounts || []).filter(a => !a.is_agency).filter(a => !q || (a.name || "").toLowerCase().includes(q.toLowerCase()));
            return (
              <div className="hds-table-wrap">
                <div className="ml-table-toolbar"><div className="ml-table-search"><Input leadingIcon="search" placeholder="Search tenants" value={q} onChange={(e) => setQ(e.target.value)} /></div></div>
                <table className="hds-table">
                  <thead><tr><th>Tenant</th><th>Apps</th><th>Members</th><th>Engagement</th><th>Open work</th><th>Status</th><th style={{ width: 60 }}></th></tr></thead>
                  <tbody>{rows.length === 0 ? <tr><td colSpan={7} className="hds-table__muted">No client tenants.</td></tr> : rows.map(t => (
                    <tr key={t.id} style={{ cursor: "pointer" }} onClick={() => go("tenant:" + t.id)}>
                      <td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><span className="ml-sitecard__flag" style={{ width: 30, height: 30, fontSize: 13 }}>{(t.name || "?")[0]}</span><span className="ml-rowtitle">{t.name}</span></div></td>
                      <td className="hds-table__muted">{fmt(t.apps)}</td>
                      <td className="hds-table__muted">{fmt(t.members)}</td>
                      <td className="hds-table__muted">{t.engagement_status ? cap(t.engagement_status) + (t.lead_name ? " · " + t.lead_name : "") : "—"}</td>
                      <td className="hds-table__muted">{fmt(t.open_work)}</td>
                      <td>{statusPill(t.status)}</td>
                      <td><div className="ml-rowactions"><IconButton icon="chevron-right" label="Open" size="sm" /></div></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            );
          })()}
        </Gate>
      </div>
    );
  }

  // One app's plans, for both grant dialogs' plan pickers. `plans === null` means
  // still loading; a failure resolves to an empty list plus a message, because a
  // plan is optional on a free grant and the dialog should say so rather than die.
  function usePlans(appId) {
    const [state, setState] = useState({ plans: appId ? null : [], err: null });
    useEffect(() => {
      if (!appId) { setState({ plans: [], err: null }); return; }
      let alive = true;
      setState({ plans: null, err: null });
      api("/agency/apps/" + encodeURIComponent(appId) + "/plans")
        .then(d => { if (alive) setState({ plans: (d && d.plans) || [], err: null }); })
        .catch(e => { if (alive) setState({ plans: [], err: (e && e.message) || "This app's plan list could not be loaded." }); });
      return () => { alive = false; };
    }, [appId]);
    return state;
  }
  const planLabelOf = (p) => p.name + " — " + money(p.monthly, "EUR") + "/mo";

  // "Mark as paid": record a payment taken outside Stripe, on a named plan. The
  // plans load before AgyFormModal mounts, because it seeds its field values once
  // (at mount) and a required Select whose options arrive later would start with
  // nothing selected. Only priced plans are offered — there is no payment to
  // record against a free one.
  function PaidAccessModal({ orgId, orgName, appId, appName, currentEnd, onClose, onDone }) {
    const Modal = window.MlBiModals._Modal;
    const { plans, err: planErr } = usePlans(appId);
    const sub = appName + " · " + orgName;
    if (plans === null) return React.createElement(Modal, { sm: true, title: "Mark as paid", sub, onClose,
      foot: <button className="bi-btn" onClick={onClose}>Cancel</button> },
      <div className="bi-modal__body"><Loading label="Loading this app's plans…" /></div>);
    // The endpoint returns every plan the app has. A free one is no use here —
    // there is no payment to record against it. The spine marks free as "no
    // Stripe price", but a plan priced for manual billing may have no Stripe
    // price either, so either signal counts as paid.
    const paid = plans.filter(p => Number(p.monthly) > 0 || !!p.stripe_price_id);
    const planOpts = paid.length
      ? paid.map(p => ({ value: String(p.id), label: planLabelOf(p) }))
      : [{ value: "", label: planErr ? "Plans unavailable" : "No priced plans for this app yet" }];
    const planName = (planId) => { const p = paid.find(x => String(x.id) === String(planId)); return p ? p.name : "the selected plan"; };
    return <AgyFormModal title="Mark as paid" sub={sub} confirmLabel="Record payment"
      fields={[
        { key: "plan_id", label: "Paid plan", required: true, options: planOpts, hint: planErr ? planErr + " Close this and try again — nothing has been changed." : "The plan they have actually paid for. It sets what they can do in the app." },
        { key: "period_end", label: "Paid up until", type: "date", required: true, value: defaultUntil(currentEnd), min: earliestEnd(), hint: "The last day the payment covers. Nothing renews by itself." },
        { key: "reason", label: "Reason / reference", required: true, value: "", placeholder: "e.g. invoice INV-2041, paid by bank transfer 12 Mar", hint: "Stored on the subscription. Name the invoice, transfer or receipt so the money can be traced back to this later." },
      ]}
      note={(v) => {
        const day = prettyDay(v.period_end);
        if (!day) return "Set the date the payment covers up to and this will spell out exactly what " + orgName + " gets.";
        return orgName + " goes onto " + planName(v.plan_id) + " for " + appName + " as soon as you save, and everyone on the account can use it straight away. This only records money you have already taken outside Stripe: no card is charged, no Stripe invoice is raised and no receipt is emailed, so do this once the payment has actually arrived. The paid period runs to " + day + " and nothing renews on its own — on " + day + " their access stops until someone records the next payment or extends it. Your reason is kept on the subscription as the record of what was paid and why.";
      }}
      onClose={onClose}
      onSubmit={async (v) => {
        if (!v.plan_id) throw new Error(paid.length ? "Pick the plan they have paid for." : "This app has no priced plan to record a payment against. Add one in the Management Panel first.");
        checkGrant(v, "period_end");
        try {
          await api("/agency/account/" + encodeURIComponent(orgId) + "/access/paid", { method: "POST", body: JSON.stringify({ app_id: appId, plan_id: v.plan_id, period_end: v.period_end, reason: v.reason.trim() }) });
        } catch (e) { throw new Error(accessErr(e)); }
        onDone();
      }} />;
  }

  // "Grant free access": comp an org. Either over a subscription they already
  // have (`sub`), or for an app they have no row for at all (`apps` = the
  // catalog minus what they already have) — in which case the grant creates the
  // subscription.
  //
  // The plan is optional upstream, and what omitting it does differs by case:
  // with an existing subscription their current plan is kept, without one they
  // land on the app's Free plan. That second case is almost never what comping a
  // pilot means, so the picker exists and the banner says which way it will go.
  function FreeAccessModal({ orgId, orgName, sub, apps, catalogErr, onClose, onDone }) {
    // The app can change while the dialog is open (new-app case), so the plan
    // list follows whatever is selected; AgyFormModal reports it back.
    const [appId, setAppId] = useState(sub ? sub.app_id : "");
    const { plans, err: planErr } = usePlans(appId);
    const appName = sub ? sub.app_name : ((apps || []).find(a => a.id === appId) || {}).name || "";
    const endsNow = sub ? prettyIso(sub.current_period_end) : "";
    const appOpts = (apps || []).length
      ? [{ value: "", label: "Choose an app…" }, ...apps.map(a => ({ value: a.id, label: a.name + (a.coming_soon ? " (coming soon)" : "") }))]
      : [{ value: "", label: catalogErr ? "The app catalog could not be loaded" : "Every app in the catalog is already on this account" }];
    // The default plan option spells out the fallback rather than hiding it in a
    // hint: comping a brand-new pilot onto Free is a mistake nobody notices
    // until the customer complains.
    const keepLabel = sub
      ? "Keep their current plan — " + (sub.plan_name || "whatever they are on")
      : (appId ? "Use the app's Free plan (limited features)" : "Pick an app first");
    const planOpts = [{ value: "", label: plans === null ? "Loading plans…" : keepLabel },
      ...(plans || []).map(p => ({ value: String(p.id), label: planLabelOf(p) }))];
    const planName = (planId) => { const p = (plans || []).find(x => String(x.id) === String(planId)); return p ? p.name : ""; };
    // A new subscription left on the Free-plan fallback is the one combination
    // worth a second look — but only once the dialog is actually filled in, so
    // the banner isn't shouting while they are still choosing.
    const risky = (v) => !sub && !v.plan_id && !!appName && !!v.until;
    return <AgyFormModal title="Grant free access" sub={sub ? sub.app_name + " · " + orgName : orgName} confirmLabel="Grant free access"
      onValues={(v) => { if (!sub) setAppId(v.app_id || ""); }}
      fields={[
        ...(sub ? [] : [{ key: "app_id", label: "App", required: true, value: "", options: appOpts, hint: catalogErr ? catalogErr + " Close this and try again — nothing has been changed." : "Only apps this client has no subscription for. Granting access creates it." }]),
        { key: "plan_id", label: "Plan to comp", value: "", options: planOpts, hint: planErr || (sub ? "Pick a plan to move them onto it as part of the comp, or leave it to comp exactly what they have now." : "Pick the plan the pilot is meant to be on. Left alone, they land on the app's Free plan.") },
        { key: "until", label: "Free until", type: "date", required: true, value: defaultUntil(sub && sub.current_period_end), min: earliestEnd(), hint: "A comp always has an end date. Pick the day it should run out." },
        { key: "reason", label: "Reason", required: true, value: "", placeholder: sub ? "e.g. goodwill after the March outage" : "e.g. 3-month pilot agreed with their CFO", hint: "Stored on the subscription and kept as the record of why this was done. Write what a colleague picking this up in six months would need to know." },
      ]}
      noteTone={(v) => (risky(v) ? "warning" : "info")}
      note={(v) => {
        const day = prettyDay(v.until);
        if (!appName) return "Pick an app and an end date and this will spell out exactly what " + orgName + " gets.";
        if (!day) return "Pick the day the free access should run out and this will spell out exactly what " + orgName + " gets.";
        const chosen = planName(v.plan_id);
        const planSentence = chosen
          ? " They go onto " + chosen + ", comped — the features of that plan, at no charge."
          : sub
            ? " They stay on the plan they are on now" + (sub.plan_name ? " (" + sub.plan_name + ")" : "") + "; comping it does not change what they can do."
            : " No plan is chosen, so they land on " + appName + "'s Free plan — the limited one. If this pilot is meant to show off a paid tier, pick that plan above instead.";
        return "Everyone at " + orgName + " gets " + appName + " free of charge from the moment you save: members already on the account are given seats, so they just open the app and it is there, and they are never asked for a card."
          + planSentence
          + (sub ? " This replaces the subscription they have now" + (endsNow ? ", which runs to " + endsNow : "") + ", so check with whoever set that up if you are not sure." : " It also creates the subscription, which they do not have today.")
          + " Access runs out on " + day + " and expiry is enforced: on that day " + appName + " stops letting them in until someone extends it. Your reason is stored on the subscription and anyone reviewing this account later will see it.";
      }}
      onClose={onClose}
      onSubmit={async (v) => {
        const app = sub ? sub.app_id : v.app_id;
        if (!app) throw new Error((apps || []).length ? "Pick the app to grant." : "There is no app left to grant — this client already has a subscription for every app in the catalog. Use Extend access or Mark as paid on the row instead.");
        checkGrant(v);
        const body = { app_id: app, until: v.until, reason: v.reason.trim() };
        if (v.plan_id) body.plan_id = v.plan_id;
        try {
          await api("/agency/account/" + encodeURIComponent(orgId) + "/access/free", { method: "POST", body: JSON.stringify(body) });
        } catch (e) { throw new Error(accessErr(e)); }
        onDone();
      }} />;
  }

  function TenantDrill({ id, go, role }) {
    const { Button, StatCard, Badge, IconButton } = DS();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [staff, setStaff] = useState([]);
    const [modal, setModal] = useState(null);
    const load = useCallback(async () => {
      setErr(null);
      try {
        const [d, asg] = await Promise.all([
          api("/agency/account/" + encodeURIComponent(id)),
          api("/agency/assignments").catch(() => ({ managers: [] })),
        ]);
        setData(d); setStaff(asg.managers || []);
      } catch (e) { setErr(e); }
      setLoading(false);
    }, [id]);
    useEffect(() => { setLoading(true); load(); }, [load]);
    // Manual access management (SD-014 §7) is owner/admin work — the same gate
    // the API applies — AND it stays dark until the backend reports its
    // Management Panel credentials are configured, so staff never get a button
    // that can only 503. Both have to be true; no dead buttons either way.
    const canManageAccess = (role === "owner" || role === "admin")
      && !!(data && data.access_management && data.access_management.enabled);
    const [busy, setBusy] = useState(false);
    const [actErr, setActErr] = useState(null);
    const actAs = async () => {
      setBusy(true); setActErr(null);
      try {
        const r = await api("/agency/impersonate/" + encodeURIComponent(id), { method: "POST", body: "{}" });
        if (r && r.redirect) { window.location = r.redirect; return; }
        setActErr("The panel didn't return a workspace to enter."); setBusy(false);
      } catch (e) { setActErr((e && e.message) || "Could not start the session."); setBusy(false); }
    };
    return (
      <div>
        <button className="hds-btn hds-btn--ghost hds-btn--sm" onClick={() => go("tenants")} style={{ marginBottom: 14 }}><MlIcon name="chevron-left" size={16} />All tenants</button>
        {actErr && <div style={{ marginBottom: 12 }}>{React.createElement(DS().Banner, { tone: "danger", title: "Couldn't act as tenant" }, actErr)}</div>}
        <Gate loading={loading} err={err}>
          {data && (() => {
            const a = data.account || {};
            const ent = a.entitlements || {};
            const eng = data.engagement;
            const subs = data.subscriptions || [];
            const work = data.work || [];
            return (
              <>
                <div className="ml-tenant-banner">
                  <span className="ml-sitecard__flag" style={{ width: 40, height: 40, fontSize: 18 }}>{(a.name || "?")[0]}</span>
                  <div className="ml-tenant-banner__meta"><div style={{ fontSize: "var(--text-lg)", fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.02em" }}>{a.name}</div><div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{ent.label || cap(a.plan)} · {eng ? "Engagement " + cap(eng.status) + (eng.lead_name || eng.lead_email ? " · " + (eng.lead_name || eng.lead_email) : "") : "Not managed"}</div></div>
                  <span className="ml-readonly-tag">Read-only</span>
                  <Button variant="primary" leadingIcon="eye" disabled={busy} onClick={actAs}>{busy ? "Starting…" : "Act as tenant"}</Button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px,1fr))", gap: 16, margin: "0 0 20px" }}>
                  <StatCard label="Apps subscribed" value={fmt(subs.length)} icon="layers" />
                  <StatCard label="Members" value={fmt((data.members || []).length)} icon="users" />
                  <StatCard label="Engagement" value={eng ? cap(eng.status) : "None"} icon="target" />
                  <StatCard label="Open work items" value={fmt(work.length)} icon="check-square" />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
                  <div className="hds-table-wrap">
                    <div className="ml-table-toolbar">
                      <strong style={{ fontSize: "var(--text-sm)" }}>Subscriptions</strong>
                      {canManageAccess && <div style={{ marginLeft: "auto" }}><Button variant="secondary" size="sm" leadingIcon="plus" onClick={async () => {
                        // Granting access to an app the org has no row for yet needs
                        // the catalog, so fetch it on open (as "New engagement" does).
                        try { const r = await api("/agency/apps"); setModal({ type: "access-new", apps: r.apps || [] }); }
                        catch (e) { setModal({ type: "access-new", apps: [], catalogErr: (e && e.message) || "The app catalog could not be loaded." }); }
                      }}>Grant app access</Button></div>}
                    </div>
                    <table className="hds-table">
                      <thead><tr><th>App</th><th>Plan</th><th>Seats</th><th>Status</th><th>Access until</th>{canManageAccess && <th style={{ width: 104 }}></th>}</tr></thead>
                      <tbody>{subs.length === 0 ? <tr><td colSpan={canManageAccess ? 6 : 5} className="hds-table__muted">No subscriptions.</td></tr> : subs.map(s => (
                        <tr key={s.id}><td className="hds-table__primary">{s.app_name}</td><td className="hds-table__muted">{s.plan_name}</td><td className="hds-table__muted">{fmt(s.seats_assigned)} / {fmt(s.seats)}</td><td>{statusPill(s.status)}</td><td>{accessUntil(s.current_period_end)}</td>
                          {canManageAccess && <td><div className="ml-rowactions ml-rowactions--always" style={{ justifyContent: "flex-end" }}>
                            <IconButton icon="calendar-clock" label={"Extend access to " + s.app_name} size="sm" onClick={() => setModal({ type: "access-extend", sub: s })} />
                            <IconButton icon="dollar" label={"Mark " + s.app_name + " as paid"} size="sm" onClick={() => setModal({ type: "access-paid", sub: s })} />
                            <IconButton icon="key" label={"Grant free access to " + s.app_name} size="sm" onClick={() => setModal({ type: "access-free", sub: s })} />
                          </div></td>}
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  <div className="hds-table-wrap">
                    <div className="ml-table-toolbar"><strong style={{ fontSize: "var(--text-sm)" }}>Members</strong></div>
                    <table className="hds-table">
                      <thead><tr><th>Member</th><th>Role</th></tr></thead>
                      <tbody>{(data.members || []).length === 0 ? <tr><td colSpan={2} className="hds-table__muted">No members.</td></tr> : data.members.map((m, i) => (
                        <tr key={i}><td className="hds-table__primary">{m.name || m.email}</td><td><Badge tone={m.role === "owner" ? "primary" : "neutral"}>{cap(m.role)}</Badge></td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
                {!eng && (
                  <div className="hds-card hds-card--pad" style={{ marginTop: 16, color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>
                    Not a managed client. Open an engagement (Users → New engagement) to manage delivery scope and work here.
                  </div>
                )}
                {eng && (() => {
                  const scopes = data.scopes || [];
                  const scopeApps = new Set(scopes.map(s => s.app_id));
                  const addable = subs.filter(s => !scopeApps.has(s.app_id));
                  const staffOpts = [{ value: "", label: "Unassigned" }, ...staff.map(s => ({ value: String(s.id), label: s.name || s.email }))];
                  return (
                    <>
                      <div className="hds-table-wrap" style={{ marginTop: 16 }}>
                        <div className="ml-table-toolbar">
                          <strong style={{ fontSize: "var(--text-sm)" }}>Delivery scope</strong>
                          <div style={{ marginLeft: "auto" }}><Button variant="secondary" size="sm" leadingIcon="plus" onClick={() => setModal({ type: "scope-add" })}>Add app to scope</Button></div>
                        </div>
                        <table className="hds-table">
                          <thead><tr><th>App</th><th>Specialist</th><th>Tenant link</th><th>Status</th><th style={{ width: 110 }}></th></tr></thead>
                          <tbody>{scopes.length === 0 ? <tr><td colSpan={5} className="hds-table__muted">No apps in scope yet — add the apps we deliver on for this client.</td></tr> : scopes.map(sc => (
                            <tr key={sc.id}>
                              <td className="hds-table__primary">{(subs.find(s => s.app_id === sc.app_id) || {}).app_name || sc.app_id}</td>
                              <td className="hds-table__muted">{sc.specialist_name || sc.specialist_email || "Unassigned"}</td>
                              <td className="hds-table__muted">{sc.external_ref ? <span style={{ fontFamily: "var(--font-mono)" }}>{sc.external_ref}</span> : <Badge tone="warning">Not linked</Badge>}</td>
                              <td><Badge tone={sc.status === "active" ? "success" : "warning"} dot>{cap(sc.status)}</Badge></td>
                              <td><div className="ml-rowactions" style={{ justifyContent: "flex-end" }}>
                                {React.createElement(DS().IconButton, { icon: "edit", label: "Edit scope", size: "sm", onClick: () => setModal({ type: "scope-specialist", sc }) })}
                                {React.createElement(DS().IconButton, { icon: sc.status === "active" ? "circle" : "play", label: sc.status === "active" ? "Pause" : "Resume", size: "sm", onClick: async () => { try { await api(`/agency/scopes/${sc.id}`, { method: "PATCH", body: JSON.stringify({ status: sc.status === "active" ? "paused" : "active" }) }); } catch { /* */ } load(); } })}
                                {React.createElement(DS().IconButton, { icon: "trash", label: "Remove", size: "sm", onClick: () => setModal({ type: "scope-remove", sc }) })}
                              </div></td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                      <div className="hds-table-wrap" style={{ marginTop: 16 }}>
                        <div className="ml-table-toolbar">
                          <strong style={{ fontSize: "var(--text-sm)" }}>Open work</strong>
                          <div style={{ marginLeft: "auto" }}><Button variant="secondary" size="sm" leadingIcon="plus" onClick={() => setModal({ type: "work-new" })}>New work item</Button></div>
                        </div>
                        <WorkTable items={work} showTenant={false} staff={staff} onReload={load} />
                      </div>
                    </>
                  );
                })()}
              </>
            );
          })()}
        </Gate>
        {modal && data && (() => {
          const eng = data.engagement;
          const subs = data.subscriptions || [];
          const scopes = data.scopes || [];
          const scopeApps = new Set(scopes.map(s => s.app_id));
          const addable = subs.filter(s => !scopeApps.has(s.app_id));
          const staffOpts = [{ value: "", label: "Unassigned" }, ...staff.map(s => ({ value: String(s.id), label: s.name || s.email }))];
          if (modal.type === "scope-add") return <AgyFormModal title="Add app to scope" sub="Which of this client's apps do we deliver on?" confirmLabel="Add"
            fields={[
              { key: "app_id", label: "App", options: addable.length ? addable.map(s => ({ value: s.app_id, label: s.app_name })) : [{ value: "", label: "All subscribed apps are already in scope" }] },
              { key: "specialist_staff_id", label: "Specialist (optional)", value: "", options: staffOpts },
              { key: "external_ref", label: "Client's tenant id in the app (optional)", value: "", placeholder: "e.g. the MarketLens account id — needed for Act as tenant", hint: "Find it in the app's own admin view; link it later from Edit scope if unknown." },
            ]}
            onClose={() => setModal(null)}
            onSubmit={async (v) => { if (!v.app_id) throw new Error("Pick an app."); await api(`/agency/assignments/${eng.id}/scopes`, { method: "POST", body: JSON.stringify(v) }); setModal(null); load(); }} />;
          if (modal.type === "scope-specialist") return <AgyFormModal title="Edit scope" sub={modal.sc.app_id + " delivery for this client"} confirmLabel="Save"
            fields={[
              { key: "specialist_staff_id", label: "Specialist", value: modal.sc.specialist_staff_id ? String(modal.sc.specialist_staff_id) : "", options: staffOpts },
              { key: "external_ref", label: "Client's tenant id in the app", value: modal.sc.external_ref || "", placeholder: "e.g. the MarketLens account id", hint: "Required before Act as tenant can enter the workspace." },
            ]}
            onClose={() => setModal(null)}
            onSubmit={async (v) => { await api(`/agency/scopes/${modal.sc.id}`, { method: "PATCH", body: JSON.stringify(v) }); setModal(null); load(); }} />;
          if (modal.type === "scope-remove" && window.MlBiModals) return React.createElement(window.MlBiModals.ConfirmDialog, {
            danger: true, title: "Remove from scope", body: `Remove ${modal.sc.app_id} from this engagement's delivery scope?`, confirmLabel: "Remove",
            onClose: () => setModal(null),
            onConfirm: async () => { try { await api(`/agency/scopes/${modal.sc.id}`, { method: "DELETE" }); } catch { /* */ } setModal(null); load(); } });
          if (modal.type === "work-new") return <AgyFormModal title="New work item" sub={"Delivery work for " + (data.account || {}).name} confirmLabel="Create"
            fields={[
              { key: "title", label: "Title", placeholder: "e.g. April content batch" },
              { key: "app_id", label: "App", value: "", options: [{ value: "", label: "No app" }, ...subs.map(s => ({ value: s.app_id, label: s.app_name }))] },
              { key: "assignee_staff_id", label: "Assignee", value: "", options: staffOpts },
              { key: "due_date", label: "Due date", type: "date", value: "" },
            ]}
            onClose={() => setModal(null)}
            onSubmit={async (v) => {
              if (!v.title || !v.title.trim()) throw new Error("Title is required.");
              await api("/agency/work", { method: "POST", body: JSON.stringify({ ...v, engagement_id: eng.id }) });
              setModal(null); load();
            }} />;

          // ---- manual access management. Guarded a second time here so a stale
          // modal can't outlive the gate (role change, feature switched off).
          if (!canManageAccess || !String(modal.type || "").startsWith("access-")) return null;
          const orgName = (data.account || {}).name || "this client";
          const done = () => { setModal(null); load(); };

          if (modal.type === "access-extend") {
            const s = modal.sub;
            const endsNow = prettyIso(s.current_period_end);
            return <AgyFormModal title="Extend access" sub={s.app_name + " · " + orgName} confirmLabel="Move the end date"
              fields={[
                { key: "until", label: "New end date", type: "date", required: true, value: defaultUntil(s.current_period_end), min: earliestEnd(), hint: endsNow ? "Currently ends " + endsNow + ". The new date has to be later than that." : "No end date is recorded yet, so this sets the first one." },
                { key: "reason", label: "Reason", required: true, value: "", placeholder: "e.g. pilot extended by a month while they decide", hint: "Stored on the subscription and kept as the record of why this was done. Write what a colleague picking this up in six months would need to know." },
              ]}
              note={(v) => {
                const day = prettyDay(v.until);
                if (!day) return "Pick the new end date and this will spell out what changes for " + orgName + ".";
                return orgName + " keeps the " + s.app_name + " access they already have — same plan, same seats, nobody signs in again and nothing changes for them today. The only thing that moves is when it runs out: " + (endsNow ? "from " + endsNow + " to " + day + "." : "it will now be recorded as ending " + day + ".") + " No money is taken and no invoice is raised. Your reason is stored on the subscription so the next person to look can see why the date moved.";
              }}
              onClose={() => setModal(null)}
              onSubmit={async (v) => {
                checkGrant(v);
                // Upstream also refuses a date that doesn't move the end forward,
                // and that arrives as a 400 after the hop. We already know the
                // current end, so answer immediately, with the same comparison.
                const curT = s.current_period_end ? new Date(s.current_period_end).getTime() : NaN;
                if (Number.isFinite(curT) && Date.parse(v.until) <= curT) throw new Error("That isn't an extension — " + s.app_name + " access already runs to " + endsNow + ". Pick a later day, or use Grant free access if you mean to replace the subscription.");
                try {
                  await api("/agency/account/" + encodeURIComponent(id) + "/access/extend", { method: "POST", body: JSON.stringify({ app_id: s.app_id, until: v.until, reason: v.reason.trim() }) });
                } catch (e) { throw new Error(accessErr(e)); }
                done();
              }} />;
          }

          if (modal.type === "access-paid") {
            const s = modal.sub;
            return <PaidAccessModal orgId={id} orgName={orgName} appId={s.app_id} appName={s.app_name} currentEnd={s.current_period_end}
              onClose={() => setModal(null)} onDone={done} />;
          }

          // Free grant, either over an existing subscription row or (access-new)
          // for an app the org has no row for yet — the grant creates it.
          if (modal.type === "access-free") return <FreeAccessModal orgId={id} orgName={orgName} sub={modal.sub} onClose={() => setModal(null)} onDone={done} />;
          if (modal.type === "access-new") {
            const subApps = new Set(subs.map(x => x.app_id));
            return <FreeAccessModal orgId={id} orgName={orgName} catalogErr={modal.catalogErr} apps={(modal.apps || []).filter(ap => !subApps.has(ap.id))} onClose={() => setModal(null)} onDone={done} />;
          }
          return null;
        })()}
      </div>
    );
  }

  // `role` is the signed-in staff member's agency role from /api/auth/me — the
  // same value main.jsx already derives `readOnly` from. The tenant drill-down
  // needs the role itself, not just read-only-ness: comping a client is
  // owner/admin work even though an Account manager can write other things.
  function AgencyApp({ onExit, onConsole, readOnly, role }) {
    const [route, setRoute] = useState("overview");
    const go = (r) => setRoute(r);
    let content;
    if (route.startsWith("tenant:")) content = <TenantDrill id={route.slice(7)} go={go} role={role} />;
    else if (route === "overview") content = <Overview go={go} />;
    else if (route === "work") content = <WorkBoard go={go} />;
    else if (route === "statistics") content = <Statistics />;
    else if (route === "users") content = <UsersScreen />;
    else if (route === "billing") content = <Billing />;
    else if (route === "activity") content = <Activity />;
    else if (route === "settings") content = <Settings />;
    else content = <Tenants go={go} />;

    const activeNav = route.startsWith("tenant:") ? "tenants" : route;

    return (
      <div className={"ml-shell" + (readOnly ? " ml-readonly" : "")}>
        <aside className="hds-sidebar ml-sidebar" style={{ width: 264 }}>
          <div className="hds-sidebar__brand"><button type="button" onClick={onExit} style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}><Brand /></button></div>
          {readOnly && <div style={{ padding: "10px 14px 0" }}><span className="ml-ro-badge"><MlIcon name="eye" size={14} />Read-only</span></div>}
          <nav className="hds-sidebar__nav" style={{ marginTop: 12 }}>
            {NAV.map((g, gi) => (
              <div className="hds-navgroup" key={gi}>
                {g.label && <div className="hds-navgroup__label">{g.label}</div>}
                {g.items.map(([id, label, icon]) => (
                  <a key={id} href="#" className={"hds-navitem " + (activeNav === id ? "hds-navitem--active" : "")} aria-current={activeNav === id ? "page" : undefined} onClick={(e) => { e.preventDefault(); go(id); }}>
                    <MlIcon name={icon} size={18} /><span>{label}</span>
                  </a>
                ))}
              </div>
            ))}
          </nav>
          <div className="hds-sidebar__footer">
            <button type="button" onClick={onConsole} className="ml-account" style={{ border: "none", background: "none", width: "100%", textAlign: "left", cursor: "pointer" }}><span className="hds-avatar hds-avatar--sm"><MlIcon name="arrow-right" size={16} /></span><span className="ml-account__meta"><span className="ml-account__name">Product apps</span><span className="ml-account__email">Open the workspace app</span></span><MlIcon name="chevron-right" size={16} /></button>
          </div>
        </aside>
        <div className="ml-shell__main">
          <main className="ml-shell__content" style={{ paddingTop: 28 }}>{content}</main>
        </div>
      </div>
    );
  }

  window.MlAgency = { AgencyApp };
})();
